import { AppProps } from "next/app";
import Head from "next/head";
import { usePostHog } from "next-use-posthog";
import { QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { Provider } from "reakit";
import { useEffect } from "react";
import queryClient from "../lib/query-client";
import { AuthTokensProvider, useCurrentUser } from "../lib/user";

declare let VERSION: string;

function PostHogIdentify(props) {
  useEffect(() => {
    if (!props.currentUser?.sub) {
      console.warn(
        "Skipping identify. There is no `sub` for current user",
        props.currentUser
      );
    } else {
      window["posthog"].identify(props.currentUser.sub, {
        ...props.currentUser,
        ["webVersion"]: VERSION,
      });
    }
  });

  return null;
}

function AnalyticsIdentifyCurrentUser() {
  const query = useCurrentUser();
  const currentUser = query.data?.currentUser;

  usePostHog("phc_8liwKIOwig8jrONTyf4Wi3qE3iS2Q4ynfcMERrWj5ML", {
    api_host: "https://app.posthog.com",
    loaded: (posthog) => {
      window["posthog"] = posthog;
    },
  });

  // Drop UTM query params
  useEffect(() => {
    if (/utm_/.test(location.search) && window.history.replaceState) {
      const oldUrl = location.href;
      let newUrl = oldUrl.replace(/\?([^#]*)/, function (_, search) {
        search = search
          .split("&")
          .map(function (v) {
            return !/^utm_/.test(v) && v;
          })
          .filter(Boolean)
          .join("&");
        return search ? "?" + search : "";
      });

      console.debug("Drop utm");

      if ((newUrl = oldUrl)) {
        setTimeout(() => {
          window.history.replaceState({}, "", newUrl);
        }, 0);
      }
    }
  }, []);

  return currentUser ? <PostHogIdentify currentUser={currentUser} /> : null;
}

export default function App({ Component, pageProps }: AppProps): JSX.Element {
  return (
    <>
      <Head>
        <meta
          name="viewport"
          content={
            "user-scalable=no, initial-scale=1, minimum-scale=1, width=device-width, height=device-height, viewport-fit=cover"
          }
        />
        <meta name="apple-mobile-web-app-status-bar-style" content="black" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-title" content="Elsponsor" />
        <meta name-="theme-color" content="#000000" />
        <link rel="shortcut icon" type="image/x-icon" href="/favicon.png" />
        <link rel="apple-touch-icon" href="/pwa/apple-touch-icon.png" />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-2048-2732.jpg"
          media="(device-width: 1024px) and (device-height: 1366px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-2732-2048.jpg"
          media="(device-width: 1024px) and (device-height: 1366px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
        />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-1668-2388.jpg"
          media="(device-width: 834px) and (device-height: 1194px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-2388-1668.jpg"
          media="(device-width: 834px) and (device-height: 1194px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
        />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-1536-2048.jpg"
          media="(device-width: 768px) and (device-height: 1024px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-2048-1536.jpg"
          media="(device-width: 768px) and (device-height: 1024px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
        />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-1668-2224.jpg"
          media="(device-width: 834px) and (device-height: 1112px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-2224-1668.jpg"
          media="(device-width: 834px) and (device-height: 1112px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
        />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-1620-2160.jpg"
          media="(device-width: 810px) and (device-height: 1080px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-2160-1620.jpg"
          media="(device-width: 810px) and (device-height: 1080px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
        />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-1284-2778.jpg"
          media="(device-width: 428px) and (device-height: 926px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-2778-1284.jpg"
          media="(device-width: 428px) and (device-height: 926px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
        />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-1170-2532.jpg"
          media="(device-width: 390px) and (device-height: 844px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-2532-1170.jpg"
          media="(device-width: 390px) and (device-height: 844px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
        />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-1125-2436.jpg"
          media="(device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-2436-1125.jpg"
          media="(device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
        />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-1242-2688.jpg"
          media="(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-2688-1242.jpg"
          media="(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
        />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-828-1792.jpg"
          media="(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-1792-828.jpg"
          media="(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
        />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-1242-2208.jpg"
          media="(device-width: 414px) and (device-height: 736px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-2208-1242.jpg"
          media="(device-width: 414px) and (device-height: 736px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
        />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-750-1334.jpg"
          media="(device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-1334-750.jpg"
          media="(device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
        />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-640-1136.jpg"
          media="(device-width: 320px) and (device-height: 568px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="/pwa/apple-splash-1136-640.jpg"
          media="(device-width: 320px) and (device-height: 568px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
        />
      </Head>
      <style jsx global>
        {`
          html,
          body,
          div#__next {
            margin: 0;
            padding: 0;
            height: 100%;
            // min-height: -webkit-fill-available;
          }
          body,
          .str-chat * {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
              "Helvetica Neue", Arial, sans-serif;

            // Remove tap highlight as we rely on each component handling its own focus/tap state
            -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

            // Prevents iOS text size adjust after orientation change, without disabling user zoom
            -webkit-text-size-adjust: 100%;
            -ms-text-size-adjust: 100%;
          }

          a,
          a:link,
          a:active,
          a:hover,
          a:visited {
            color: #0070f3;
          }
          ul,
          li,
          ol {
            list-style: none;
          }
          * {
            box-sizing: border-box;
          }

          @media (any-pointer: coarse) {
            *:focus {
              outline: none;
            }
          }

          @font-face {
            font-family: "Thicccboi";
            src: url("/fonts/THICCCBOI-Black.woff2") format("woff2"); // TODO: Build fonts into renderer
          }
        `}
      </style>
      <Provider>
        <AuthTokensProvider>
          <QueryClientProvider client={queryClient}>
            <AnalyticsIdentifyCurrentUser />
            <Component {...pageProps} />
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </AuthTokensProvider>
      </Provider>
    </>
  );
}
